import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { useHMSActions } from "@100mslive/react-sdk";
import { EmojiIcon, SendIcon } from "@100mslive/react-icons";
import {
  Box,
  Flex,
  IconButton,
  Popover,
  styled,
} from "@100mslive/roomkit-react";
import { ToastManager } from "../Toast/ToastManager";
import { useChatDraftMessage } from "../AppData/useChatState";
import { useEmojiPickerStyles } from "./useEmojiPickerStyles";

const TextArea = styled("textarea", {
  width: "100%",
  fontFamily: "$sans",
  bg: "transparent",
  color: "$on_surface_high",
  resize: "none",
  lineHeight: "1rem",
  position: "relative",
  top: "$3",
  "&:focus": {
    boxShadow: "none",
    outline: "none",
  },
});

function EmojiPicker({ onSelect }) {
  const [showEmoji, setShowEmoji] = useState(false);
  const ref = useEmojiPickerStyles(showEmoji);

  const toggle = () => {
    setShowEmoji(s => !s);
  };

  return (
    <div style={{ position: "relative" }}>
      <IconButton
        onClick={toggle}
        aria-expanded={showEmoji}
        aria-haspopup="dialog"
      >
        <EmojiIcon />
      </IconButton>
      {showEmoji ? (
        <Box
          ref={ref}
          css={{
            position: "absolute",
            bottom: "calc(100% + $4)",
            right: 0,
            p: 0,
            zIndex: 9999,
            minWidth: 352,
            minHeight: 435,
            bg: "$surface_bright",
          }}
          onPointerDownCapture={e => {
            e.stopPropagation();
          }}
        >
          <Picker
            onEmojiSelect={emoji => {
              onSelect?.(emoji);
              setShowEmoji(false);
            }}
            data={data}
            previewPosition="none"
            skinPosition="search"
          />
        </Box>
      ) : null}
    </div>
  );
}

export const ChatFooter = ({ role, peerId, onSend, children }) => {
  const hmsActions = useHMSActions();
  const inputRef = useRef(null);
  const [draftMessage, setDraftMessage] = useChatDraftMessage();
  const { t } = useTranslation();

  const sendMessage = useCallback(async () => {
    const message = inputRef.current?.value;
    if (!message || !message.trim().length) {
      return;
    }
    try {
      if (role) {
        await hmsActions.sendGroupMessage(message, [role]);
      } else if (peerId) {
        await hmsActions.sendDirectMessage(message, peerId);
      } else {
        await hmsActions.sendBroadcastMessage(message);
      }
      inputRef.current && (inputRef.current.value = "");
      setTimeout(() => {
        onSend?.();
      }, 0);
    } catch (error) {
      ToastManager.addToast({ title: error.message });
    }
  }, [role, peerId, hmsActions, onSend]);

  useEffect(() => {
    const messageElement = inputRef.current;
    messageElement && (messageElement.value = draftMessage);
  }, [draftMessage]);

  useEffect(() => {
    const messageElement = inputRef.current;
    return () => {
      setDraftMessage(messageElement?.value || "");
    };
  }, [setDraftMessage]);

  return (
    <Flex
      align="center"
      css={{
        bg: "$surface_bright",
        minHeight: "$16",
        maxHeight: "$24",
        position: "relative",
        py: "$6",
        pl: "$8",
        r: "$1",
      }}
    >
      {children}
      <TextArea
        placeholder={t("lbl_write_smt_here")}
        ref={inputRef}
        autoFocus
        css={{ fontSize: "1rem" }}
        onKeyPress={async event => {
          if (event.key === "Enter") {
            if (!event.shiftKey) {
              event.preventDefault();
              await sendMessage();
            }
          }
        }}
        autoComplete="off"
        aria-autocomplete="none"
        onPaste={e => e.stopPropagation()}
        onCut={e => e.stopPropagation()}
        onCopy={e => e.stopPropagation()}
      />
      <EmojiPicker
        onSelect={emoji => {
          inputRef.current && (inputRef.current.value += ` ${emoji.native} `);
        }}
      />
      <IconButton
        onClick={sendMessage}
        css={{ ml: "auto", height: "max-content", mr: "$4" }}
        data-testid="send_msg_btn"
      >
        <SendIcon />
      </IconButton>
    </Flex>
  );
};
